import fb from '@/firebase'
import Vue from 'vue'
import { ActionContext } from 'vuex'
import { CastTemplatesState, RootState, FirestoreQuerySnapper, FirestoreSnapperType } from '../types'
import { Cast, InvitedCaster } from '@/types/casts'
import { moduleActionContext, storehelper } from '@/store'
import { firestore } from 'firebase/app'
import { StreamType } from '@/types/streams'
import store from '..'
import { CastTemplate } from '@/types/casttemplates'
import { versionToInt } from '@/classes/versions'
type CastTemplatesContext = ActionContext<CastTemplatesState, RootState>

// initial state
const state = {
  templates: []
} as CastTemplatesState

// actions
const actions = {
  async subscribeTeamCastTemplates (context: CastTemplatesContext, teamId: string) {
    const { commit } = castTemplatesModuleActionContext(context)
    const snapper: FirestoreQuerySnapper = {
      query: fb.db
               .collection('cast_templates')
               .where('team_id', '==', teamId)
               .where('deleted', '==', false),
      mutation: commit.setCastTemplates,
      type: FirestoreSnapperType.Query
    }
    storehelper.subscribe(`teamcasttemplates`, snapper)
  },
  async unsubscribeTeamCastTemplates (_context: CastTemplatesContext) {
    storehelper.unsubscribe(`teamcasttemplates`)
  },
  async saveCastAsTemplate ({ rootState }: CastTemplatesContext, cast: Cast) {
    const tmpCast = JSON.parse(JSON.stringify(cast))
    const currentUserId = rootState.user.id || ''
    delete tmpCast.ngcvp_status
    delete tmpCast.chat_id
    delete tmpCast.event_id
    delete tmpCast.start_date
    delete tmpCast.end_date
    delete tmpCast.k360_event_id
    delete tmpCast.m3u8
    delete tmpCast.mp4
    delete tmpCast.status
    delete tmpCast.id
    delete tmpCast.video_start_time
    delete tmpCast.originalCast
    delete tmpCast.clips
    delete tmpCast.isos
    delete tmpCast.feedname

    for (const caster of Object.values(cast.invited_casters)) {
      if (!caster.anonymous) continue
      delete tmpCast.invited_casters[caster.userId]
    }

    tmpCast.deleted = false
    tmpCast.copying = false
    tmpCast.online_sessions = {}
    tmpCast.output_streams = {}
    tmpCast.preview_scenes = {}
    tmpCast.source_streams = {}
    tmpCast.talk_back = {}
    tmpCast.created_date = firestore.Timestamp.now()
    tmpCast.created_by = currentUserId

    // cleanup the scenes so no casters are left in by mistake
    Object.keys(tmpCast.scenes).forEach(sceneKey => {
      if (!tmpCast.scenes[sceneKey].contents) return
      const keys = Object.keys(tmpCast.scenes[sceneKey].contents)
      for (const key of keys) {
        const tmpContent = tmpCast.scenes[sceneKey].contents[key]
        if (tmpContent.type === StreamType.Caster) {
          delete tmpCast.scenes[sceneKey].contents[key]
        }
      }
    })

    // for legacy casts under the old format
    if (tmpCast.input_stream !== undefined && tmpCast.input_streams === undefined) {
      tmpCast.input_streams = { [tmpCast.input_stream]: true }
    }

    return await fb.db.collection('cast_templates').doc().set(tmpCast)
  },
  async updateCastTemplate ({}: CastTemplatesContext, template) {
    const templateId = template.id
    return await fb.db
      .collection('cast_templates')
      .doc(templateId)
      .set(template, { merge: true })
  },
  async deleteCastTemplate ({}: CastTemplatesContext, template) {
    return await fb.db
      .collection('cast_templates')
      .doc(template)
      .set({ deleted: true }, { merge: true })
  },
  async cleanupTeamTemplates ({}: CastTemplatesContext, { id, itemType }: { id: string; itemType: string }) {
    // itemType = either asset_groups, invited_casters, input_streams, custom_rtmp_destinations
    const team = store.state.team.activeTeam
    const templates = await fb.db
      .collection('cast_templates')
      .where('team_id', '==', team)
      .where('deleted', '==', false)
      .get()
    for (let i = 0; i < templates.docs.length; i++) {
      const templateId = templates.docs[i].id
      const template = templates.docs[i].data()
      if (template[itemType][id] !== undefined) {
        const ss = `${itemType}.${id}`
        fb.db
          .collection('cast_templates')
          .doc(templateId)
          .update({
            [ss]: fb.firebase.firestore.FieldValue.delete()
          })
          .catch((error) => {
            console.error('Cleanup CastTemplate fields', error)
          })
      }
    }
  }
}

// mutations
const mutations = {
  setCastTemplates (state: CastTemplatesState, snap: firestore.QuerySnapshot) {
    const version = versionToInt(store.state.team.team.version)
    const templates : CastTemplate[] = []
    snap.forEach((doc) => {
      const data = doc.data()
      data.id = doc.id
      const template = data as CastTemplate

      for (const [key, value] of Object.entries(template.invited_casters)) {
        template.invited_casters[key] = new InvitedCaster({ ...value, userId: key })
      }

      if (template.version === undefined || versionToInt(template.version) <= version) {
        templates.push(template)
      } else {
        console.warn(`Did not add template ${template.name} to the list, it is too new  (${template.version})`)
      }
    })
    Vue.set(state, 'templates', templates)
  }
}

const castTemplatesModule = {
  namespaced: true,
  state,
  actions,
  mutations
} as const

export default castTemplatesModule
export const castTemplatesModuleActionContext = (context) => moduleActionContext(context, castTemplatesModule)
